import { useState } from 'react';
import { useFocusMode, PATH_TYPES } from '../context/FocusModeContext';
import { Button } from './ui/button';
import { X, Focus, ChevronRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

// Banner shown when Focus Mode is active
export const FocusModeBanner = () => {
  const { focusModeEnabled, focusedPathType, focusedPathName, disableFocusMode, loading, getCurrentPathConfig, isFocusModeLocked } = useFocusMode();

  if (!focusModeEnabled) return null;

  // District students get a minimal locked banner instead of the full one
  if (isFocusModeLocked) {
    const pathConfig = getCurrentPathConfig();
    return (
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-5 px-4 py-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center gap-3"
      >
        <span className="text-lg">{pathConfig?.icon || '🏫'}</span>
        <div className="flex-1 min-w-0">
          <p className="text-cyan-300 text-sm font-medium">NC District Student View</p>
          <p className="text-slate-300 text-xs">Your school district has set this view. Contact your counselor to change it.</p>
        </div>
      </motion.div>
    );
  }

  const pathConfig = getCurrentPathConfig();
  const gradientClass = pathConfig?.gradient || 'from-blue-600 to-cyan-600';

  const handleDisable = async () => {
    const success = await disableFocusMode();
    if (success) {
      toast.success('Focus Mode disabled - all paths are now visible');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className={`mb-6 p-4 rounded-xl bg-gradient-to-r ${gradientClass} relative overflow-hidden`}
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl animate-pulse" />
        <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-white/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
            <Focus className="w-6 h-6 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-2xl">{pathConfig?.icon}</span>
              <h3 className="text-white font-semibold text-lg">Focus Mode: {focusedPathName}</h3>
            </div>
            <p className="text-white/80 text-sm mt-1">
              Your navigation is optimized for your {focusedPathName?.toLowerCase()} journey
            </p>
          </div>
        </div>
        
        <Button
          onClick={handleDisable}
          disabled={loading}
          variant="ghost"
          className="text-white hover:bg-white/20 border border-white/30"
          data-testid="disable-focus-mode-btn"
        >
          <X className="w-4 h-4 mr-2" />
          Exit Focus Mode
        </Button>
      </div>
    </motion.div>
  );
};

// Path selection modal/card for choosing focus
export const FocusModeSelector = ({ onSelect, compact = false }) => {
  const { enableFocusMode, focusModeEnabled, loading } = useFocusMode();
  const [hoveredPath, setHoveredPath] = useState(null);

  const handleSelectPath = async (pathType) => {
    const pathConfig = PATH_TYPES[pathType];
    const success = await enableFocusMode(pathType, pathConfig.name);
    if (success) {
      toast.success(`Focus Mode enabled: ${pathConfig.name}`);
      if (onSelect) onSelect(pathType);
    }
  };

  if (focusModeEnabled) return null;

  const pathList = Object.values(PATH_TYPES);

  if (compact) {
    return (
      <div className="glass-card p-4 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Enable Focus Mode</h3>
            <p className="text-slate-300 text-xs">Simplify your experience by choosing your primary path</p>
          </div>
        </div>
        
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
          {pathList.map((path) => (
            <button
              key={path.id}
              onClick={() => handleSelectPath(path.id)}
              disabled={loading}
              className={`p-3 rounded-lg bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 hover:border-slate-600 transition-all text-center group`}
              data-testid={`focus-path-${path.id}`}
            >
              <span className="text-2xl block mb-1 group-hover:scale-110 transition-transform">{path.icon}</span>
              <span className="text-xs text-slate-300">{path.name}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-6 mb-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
          <Sparkles className="w-8 h-8 text-white" />
        </div>
        <h2 className="text-xl font-semibold text-white mb-2">Choose Your Focus</h2>
        <p className="text-slate-300 text-sm max-w-md mx-auto">
          Select your primary career path to get a personalized, distraction-free experience. 
          You can always change this later.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pathList.map((path) => (
          <motion.button
            key={path.id}
            onClick={() => handleSelectPath(path.id)}
            disabled={loading}
            onMouseEnter={() => setHoveredPath(path.id)}
            onMouseLeave={() => setHoveredPath(null)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`relative p-5 rounded-xl bg-slate-800/50 hover:bg-slate-700/50 border border-slate-700/50 hover:border-slate-600 transition-all text-left group overflow-hidden`}
            data-testid={`focus-path-${path.id}`}
          >
            {/* Gradient overlay on hover */}
            <div className={`absolute inset-0 bg-gradient-to-br ${path.gradient} opacity-0 group-hover:opacity-10 transition-opacity`} />
            
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <span className="text-3xl">{path.icon}</span>
                <ChevronRight className={`w-5 h-5 text-slate-300 group-hover:text-white transition-all ${hoveredPath === path.id ? 'translate-x-1' : ''}`} />
              </div>
              <h3 className="text-white font-semibold mb-1">{path.name}</h3>
              <p className="text-slate-300 text-sm">{path.description}</p>
            </div>
          </motion.button>
        ))}
      </div>

      <p className="text-center text-slate-300 text-xs mt-4">
        Focus Mode hides irrelevant navigation options and customizes your AI recommendations
      </p>
    </div>
  );
};

// Simple toggle for the profile/settings page
export const FocusModeToggle = () => {
  const { focusModeEnabled, focusedPathName, disableFocusMode, loading } = useFocusMode();
  const [showSelector, setShowSelector] = useState(false);

  if (focusModeEnabled) {
    return (
      <div className="glass-card p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold">Focus Mode</h3>
            <p className="text-slate-300 text-sm">Currently focused on: {focusedPathName}</p>
          </div>
          <Button
            onClick={disableFocusMode}
            disabled={loading}
            variant="outline"
            className="border-slate-700"
            data-testid="focus-mode-toggle-off"
          >
            Disable
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-white font-semibold">Focus Mode</h3>
          <p className="text-slate-300 text-sm">Simplify your navigation by focusing on one path</p>
        </div>
        <Button
          onClick={() => setShowSelector(!showSelector)}
          variant="outline"
          className="border-slate-700"
          data-testid="focus-mode-toggle-on"
        >
          {showSelector ? 'Cancel' : 'Enable'}
        </Button>
      </div>
      
      <AnimatePresence>
        {showSelector && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <FocusModeSelector compact onSelect={() => setShowSelector(false)} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FocusModeBanner;
